import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface Member {
  id: string;
  name: string;
  email: string;
  is_admin?: boolean;
}

interface AuthContextType {
  member: Member | null;
  loading: boolean;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  member: null,
  loading: true,
  logout: async () => {},
  refreshSession: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/verify-session', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setMember(data.member);
      } else {
        setMember(null);
      }
    } catch {
      setMember(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const logout = async () => {
    try {
      await fetch('/.netlify/functions/verify-session', {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch {
      // proceed with local logout regardless
    }
    setMember(null);
  };

  return (
    <AuthContext.Provider value={{ member, loading, logout, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};
